import { chakra } from '@chakra-ui/react';
import { type ReactNode } from 'react';
import { IconButton } from '../primitives/IconButton';
import { Icon, Icons } from '../icons';
import { NavItem } from './NavItem';
import { isActivePath, type LinkComponent, type NavModelItem } from './types';
import { usePersistentBoolean } from './usePersistentState';

const EXPANDED_W = '15rem'; // 240px
const COLLAPSED_W = '4.25rem'; // 68px

const Rail = chakra('nav', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    h: '100%',
    bg: 'surface.default',
    borderRightWidth: '1px',
    borderColor: 'border.subtle',
    transitionProperty: 'width',
    transitionDuration: 'normal',
    transitionTimingFunction: 'standard',
  },
});

const Head = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    minH: '14',
    px: '3',
    borderBottomWidth: '1px',
    borderColor: 'border.subtle',
  },
});

const List = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '1', p: '2', flex: '1', overflowY: 'auto' },
});

const Foot = chakra('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1',
    p: '2',
    borderTopWidth: '1px',
    borderColor: 'border.subtle',
  },
});

export interface NavRailProps {
  items: NavModelItem[];
  /** Active-route source (feature layer passes the TanStack Router pathname). */
  currentPath: string;
  /** Router link component; each item renders as `<linkComponent to=… />`. */
  linkComponent?: LinkComponent;
  /** Brand mark / header (hidden text when collapsed is the caller's concern). */
  header?: ReactNode;
  /** Persistent footer slots (theme toggle, sign-out, etc.). */
  footer?: ReactNode;
  /** localStorage key for the collapsed preference. */
  storageKey?: string;
  defaultCollapsed?: boolean;
  /** Called after any item is activated (e.g. to close a mobile drawer). */
  onNavigate?: () => void;
}

/**
 * The compact desktop navigation rail (§ "Operator's Console"). Icon+label items that
 * collapse to icons-only with tooltips; the collapsed state persists across reloads.
 * Route-aware: the active item is derived from `currentPath` and marked `aria-current`.
 */
export function NavRail({
  items,
  currentPath,
  linkComponent,
  header,
  footer,
  storageKey = 'ehe.nav.collapsed',
  defaultCollapsed = false,
  onNavigate,
}: NavRailProps) {
  const [collapsed, , toggle] = usePersistentBoolean(storageKey, defaultCollapsed);

  return (
    <Rail
      width={collapsed ? COLLAPSED_W : EXPANDED_W}
      aria-label="Primary"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      {header && <Head justifyContent={collapsed ? 'center' : 'flex-start'}>{header}</Head>}

      <List>
        {items.map((item) => (
          <NavItem
            key={item.key}
            label={item.label}
            icon={item.icon}
            badge={item.badge}
            active={isActivePath(currentPath, item.to)}
            collapsed={collapsed}
            as={linkComponent}
            to={item.to}
            onClick={onNavigate}
            data-testid={item['data-testid']}
          />
        ))}
      </List>

      <Foot>
        {footer}
        <IconButton
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          data-testid="nav-collapse-toggle"
          variant="ghost"
          onClick={toggle}
          icon={
            <Icon as={collapsed ? Icons.PanelLeftOpen : Icons.PanelLeftClose} size="md" tone="current" />
          }
        />
      </Foot>
    </Rail>
  );
}
